'use client';

import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

export interface ModelShelfThumbnailProps {
  url: string;
  className?: string;
  /** Capture after the model has been framed + this many ms of settle time. */
  autoCaptureDelayMs?: number;
  onAutoCaptured?: (blob: Blob | null) => void;
}

/**
 * Captures once after `delayMs`, once `armed` is true (model framed).
 */
function AutoCapture({
  armed,
  delayMs,
  onCaptured,
}: {
  armed: boolean;
  delayMs: number;
  onCaptured: (blob: Blob | null) => void;
}) {
  const { gl, scene, camera, invalidate } = useThree();
  useEffect(() => {
    if (!armed) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      for (let i = 0; i < 4; i++) {
        invalidate();
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (cancelled) return;
      }
      gl.render(scene, camera);
      gl.domElement.toBlob(
        (b) => {
          if (!cancelled) onCaptured(b);
        },
        'image/jpeg',
        0.78,
      );
    }, delayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [armed, delayMs, onCaptured, gl, scene, camera, invalidate]);
  return null;
}

function FitModel({
  url,
  onFramed,
}: {
  url: string;
  onFramed: () => void;
}) {
  const { scene } = useGLTF(url);
  const { camera, invalidate } = useThree();
  const framedRef = useRef(false);

  const root = useMemo(() => {
    const group = new THREE.Group();
    const clone = scene.clone(true);
    clone.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = false;
        mesh.receiveShadow = false;
      }
    });
    group.add(clone);
    const box = new THREE.Box3().setFromObject(clone);
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3());
      clone.position.sub(center);
    }
    return group;
  }, [scene]);

  useLayoutEffect(() => {
    const box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z, 0.001);
    const dist = maxDim * 1.65;
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.position.set(dist * 0.82, dist * 0.52, dist);
      camera.near = Math.max(0.01, dist / 200);
      camera.far = Math.max(50, dist * 25);
      camera.lookAt(0, size.y * 0.04, 0);
      camera.updateProjectionMatrix();
    }
    invalidate();
    if (!framedRef.current) {
      framedRef.current = true;
      // Let a couple frames paint embedded textures before arming capture.
      requestAnimationFrame(() => requestAnimationFrame(() => onFramed()));
    }
  }, [root, camera, invalidate, onFramed]);

  useEffect(() => {
    return () => {
      root.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.geometry?.dispose?.();
          const mat = mesh.material;
          const disposeMat = (m: THREE.Material) => m.dispose();
          if (Array.isArray(mat)) mat.forEach(disposeMat);
          else if (mat) disposeMat(mat);
        }
      });
    };
  }, [root]);

  return <primitive object={root} />;
}

function DemandKick({ bootKey }: { bootKey: string }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    invalidate();
    const timers = [80, 240, 600, 1200].map((ms) => setTimeout(() => invalidate(), ms));
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [bootKey, invalidate]);
  return null;
}

/**
 * Tiny demand-loop GLB preview for the asset shelf. Frames the model, then optionally
 * auto-captures a JPEG so the WebGL context can be freed.
 */
export function ModelShelfThumbnail({
  url,
  className,
  autoCaptureDelayMs = 500,
  onAutoCaptured,
}: ModelShelfThumbnailProps) {
  const [framed, setFramed] = useState(false);
  const onFramed = useMemo(() => () => setFramed(true), []);

  useEffect(() => {
    setFramed(false);
  }, [url]);

  return (
    <div className={className} style={{ width: '100%', height: '100%', minHeight: 0 }}>
      <Canvas
        className="block h-full w-full"
        shadows={false}
        frameloop="demand"
        dpr={1}
        camera={{ fov: 35, near: 0.01, far: 100, position: [1.4, 0.9, 1.6] }}
        gl={{
          alpha: false,
          antialias: true,
          powerPreference: 'low-power',
          preserveDrawingBuffer: true,
        }}
      >
        <color attach="background" args={['#1c1c20']} />
        <ambientLight intensity={0.55} />
        <hemisphereLight args={['#e8eef8', '#2a2a30', 0.5]} />
        <directionalLight position={[2.4, 3.6, 2]} intensity={1.05} />
        <directionalLight position={[-2, 1.2, -1.5]} intensity={0.35} />
        <Suspense fallback={null}>
          <FitModel url={url} onFramed={onFramed} />
        </Suspense>
        <DemandKick bootKey={url} />
        {onAutoCaptured ? (
          <AutoCapture
            armed={framed}
            delayMs={autoCaptureDelayMs}
            onCaptured={onAutoCaptured}
          />
        ) : null}
      </Canvas>
    </div>
  );
}
